import Modal from "@/components/atoms/Modal";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import useLoadOptions from "@/hooks/useLoadOptions";
import {
  CompanyCoursePeopleFilters,
  CompanyCoursePersonForm,
} from "@/interfaces/CompanyCourse";
import { IFilters } from "@/interfaces/Fetch";
import { IModalMethods } from "@/interfaces/Modal";
import { useStoreCompanyCoursePersonMutation } from "@/services/companies/courses/people";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

const StoreCompanyCoursePersonModal: ForwardRefRenderFunction<
  IModalMethods,
  { companyId: string; courseId: string }
> = ({ companyId, courseId }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const { loadCompanyPeopleOptions } = useLoadOptions();
  const defaultAdditional = useRef<CompanyCoursePeopleFilters>({
    pageIndex: 0,
    companyId,
  }).current;

  const { control, handleSubmit, reset } = useForm<CompanyCoursePersonForm>();
  const [storeCompanyCoursePersonAsync] = useStoreCompanyCoursePersonMutation();

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<CompanyCoursePersonForm> = ({ persons }) => {
    toast.promise(
      storeCompanyCoursePersonAsync([
        companyId,
        courseId,
        {
          personIds: persons.map((element) => element.id),
        },
      ]).unwrap(),
      {
        pending: "Ajout de l'apprenant en cours...",
        success: {
          render() {
            showOrHiddenModal();
            reset();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                L'apprenant à bien été ajouté 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  const onError: SubmitErrorHandler<CompanyCoursePersonForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Ajouter un apprenant"
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={12}>
          <Form.Group>
            <Form.Label>
              Personne(s) <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              control={control}
              name="persons"
              rules={{ required: true }}
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadCompanyPeopleOptions}
                  getOptionLabel={(option) => option.fullname}
                  getOptionValue={(option) => option.id.toString()}
                  value={field.value}
                  isMulti
                  onChange={field.onChange}
                  additional={defaultAdditional}
                />
              )}
            />
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreCompanyCoursePersonModal);
